import httpClient from "@plugins/http";
import { notNull } from "@utils/var.check";
import { ApiResponse } from "@apis/api.ts";
import { LessonBasicInfo } from "@modules/assignment/assignment-types.ts";
import { LessonResourceApiModel } from "@plugins/google/types.ts";
import { LessonActivityComponentModel } from "@modules/editor/types.ts";
import { LessonAnchorModel } from "@plugins/dynamic-link/types.ts";

interface ActivityTeamEligibilityResult {
  activityId: number;
  eligibleForTeamActivity: boolean;
  blockedQuestionIds?: number[];
}

interface ActivityTeamEligibilityCheckResponse {
  results: ActivityTeamEligibilityResult[];
}

/**
 * TODO: [API] this API contains too many method, split it out to category into more relevant group
 * i.e.
 * - editor.lesson.meta
 * - editor.lesson.activity
 * - editor.lesson.component
 */

const copyLesson = async (params): Promise<ApiResponse<Record<string, string>>> => {
  return await httpClient.post(`/apis/lesson/node/duplicate`, params);
};

export default {
  /**
   * lesson api - lesson
   */
  copyLesson,
  // TODO: [API] rename api url - should not be using /my-drive/
  // TODO: [API] remove url variable
  fetchLesson: (params) => httpClient.get(`/apis/my-drive/lesson/${params.uuid}`),

  fetchManageLessonList: (requestData) => {
    const data = new URLSearchParams();
    Object.keys(requestData).forEach((key) => {
      if (notNull(requestData[key])) {
        data.append(key, requestData[key]);
      }
    });
    return httpClient.get(`/apis/lesson/manage-resource/list`, {
      params: data,
    });
  },
  fetchLessonUserPermissions: (params) => {
    const resourceDomain = params.resourceDomain;
    const uuid = params.uuid;
    delete params.resourceDomain;
    delete params.uuid;
    return httpClient.get(`/apis/${resourceDomain}/lesson/get-permissions/${uuid}`, {
      params: {
        ...params,
        t: new Date().getTime().toString(),
      },
    });
  },
  // TODO: [API] rename to /apis/lesson/alpstage/list
  fetchAlpStages: () => httpClient.get(`/apis/alpstage/list`),

  fetchLearningTypes: () => httpClient.get(`/apis/lesson/learningtype/list`),

  /**
   * Get details of specific lesson
   */
  fetchLessonDetails: ({ uuid, additionalDetails }) => {
    return httpClient.get(`/apis/lesson/get-details/${uuid}`, {
      params: {
        additionalDetails: additionalDetails,
      },
    });
  },

  /**
   * Get details for manage resource listing
   */
  fetchManageLessonDetails: ({ uuid, additionalDetails }) => {
    return httpClient.get(`/apis/lesson/manage-resource/get-details/${uuid}?`, {
      params: {
        additionalDetails: additionalDetails,
      },
    });
  },
  /**
   * Get details for quizActivity
   */
  fetchQuizActivityMetadata: (uuid) => {
    return httpClient.get(`/apis/activity-quiz/${uuid}`);
  },
  createCourse: (learningType, folderUuid, title, courseCreationOption) => {
    const params = new URLSearchParams();
    params.append("learningType", learningType);
    params.append("folderUuid", folderUuid);
    params.append("title", title);
    params.append("courseCreationOption", courseCreationOption);
    return httpClient.post(`/apis/course/create?${params}`);
  },
  /**
   * Use when
   * - create new lesson with ALP template
   */
  createLessonWithTemplate: (
    templateUuid,
    folderUuid,
    title,
    activitiesToDuplicate,
    sectionsToDuplicate,
  ) => {
    const params = new URLSearchParams();
    params.append("templateUuid", templateUuid);
    params.append("folderUuid", folderUuid);
    params.append("title", title);
    params.append("activitiesToDuplicate", activitiesToDuplicate ?? []);
    params.append("sectionsToDuplicate", sectionsToDuplicate ?? []);
    return httpClient.post(`/apis/lesson/create/with-template?${params}`);
  },
  createLessonWithSourceLesson: (
    sourceLessonUuid,
    folderUuid,
    activitiesToDuplicate,
    sectionsToDuplicate,
  ) => {
    const formData = new FormData();
    formData.append("lessonUuid", sourceLessonUuid);
    formData.append("folderUuid", folderUuid);
    formData.append("activitiesToDuplicate", activitiesToDuplicate ?? []);
    formData.append("sectionsToDuplicate", sectionsToDuplicate ?? []);
    return httpClient.post(`/apis/lesson/create/with-source-lesson`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },

  saveLesson: (params) => {
    return httpClient.post(`/apis/lesson/save`, params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  copyMOELesson(params) {
    return httpClient.post(`/apis/lesson/moe/copy-to-drive`, params);
  },

  getAsyncDuplicationStatus: (params) => {
    const timeStamp = Date.now();
    return httpClient.get(
      `/apis/lesson/duplication-status?duplicationUuid=${params}&t=${timeStamp}`,
    );
  },

  /**
   * lesson api - lesson section
   */
  deleteSection: (params) => {
    return httpClient.post(`/apis/section/delete`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  reorderSection: (params) => {
    return httpClient.post(`/apis/section/reorder`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  reorderSectionActivity: (requestData) => {
    return httpClient.post("/apis/section/reorder-activity", requestData);
  },
  saveLessonActivitySequence: ({ lessonUuid, requestData }): Promise<ApiResponse> => {
    return httpClient.post(`/apis/lesson/activity/save-sequence/${lessonUuid}`, requestData);
  },
  /**
   * lesson api - lesson activity
   */
  addPageAndSection: (params) => {
    return httpClient.post("/apis/lesson/add-page-section", params);
  },
  deleteActivity: (params) => {
    return httpClient.post("/apis/lesson/deletepage", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  deleteSectionActivity: (params) => {
    return httpClient.post("/apis/section/remove-activity", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  saveActivityHeaderMetadata: (params) => {
    return httpClient.post("/apis/lesson/saveactivityheadermetadata", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  saveQuizActivityHeaderMetadata: (params) => {
    return httpClient.post("/apis/lesson/savepageheadermetadata", params);
  },
  saveQuizActivityCoverMetadata: (params) => {
    return httpClient.post("/apis/lesson/savepagecovermetadata", params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  saveActivityDetails: (lessonActivityId, params) => {
    return httpClient.post(`/apis/lesson/activity-details/save/${lessonActivityId}`, params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  saveActivityOptional: (params) => {
    return httpClient.post("/apis/lesson/activity/save-optional", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  reorderActivity: (params) => {
    return httpClient.post(`/apis/lesson/reorderpage`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  copyPages: (requestData) => {
    return httpClient.post("/apis/lesson/copy-pages", requestData);
  },
  copyPagesToSection: (requestData) => {
    return httpClient.post("/apis/section/copy-activity", requestData);
  },
  copyPagesToNewFirstSection: (requestData) => {
    return httpClient.post("/apis/section/create/copy-activity", requestData);
  },
  copySectionsToLesson: (requestData) => {
    return httpClient.post("/apis/section/copy-template-sections", requestData);
  },
  saveSection: (params) => {
    return httpClient.post("/apis/section/save", params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  getPage: (activityId) => {
    return httpClient.get("/apis/lesson/get-page", {
      params: {
        activityId: activityId,
      },
    });
  },

  /**
   * lesson api - lesson component
   */
  createContainer: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/createcontainer/${lessonUuid}`, params, {});
  },
  createPageBreak: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/page/break/${lessonUuid}`, params, {});
  },
  duplicatePageComponent: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/page/component/duplicate/${lessonUuid}`, params, {});
  },
  moveQuestionNextPreviousPage: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/move/question/${lessonUuid}`, params, {});
  },
  moveQuestionSpecificPage: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/move/question/specific/page/${lessonUuid}`, params, {});
  },
  deletePage: (lessonUuid, params) => {
    const requestParams = new URLSearchParams();
    requestParams.append("containerId", params.containerId);
    return httpClient.post(`/apis/lesson/page/delete/${lessonUuid}?${requestParams}`);
  },
  saveComponent: (
    lessonUuid: string,
    params: Partial<LessonActivityComponentModel>,
  ): Promise<Record<string, unknown>> => {
    return httpClient.post(`/apis/lesson/savecomponent/${lessonUuid}`, params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  saveDuplicateComponent: (uuid, params) => {
    return httpClient.post(`/apis/lesson/saveduplicate/${uuid}`, params, {});
  },
  saveDuplicateMultipleComponent: (params) => {
    return httpClient.post(`/apis/lesson/saveduplicates`, params, {});
  },
  removeComponent: (uuid, componentId) => {
    return httpClient.post(`/apis/lesson/removecomponent/${uuid}`, componentId, {});
  },
  duplicateComponent: (
    lessonUuid: string,
    params: LessonActivityComponentModel,
  ): Promise<ApiResponse<LessonActivityComponentModel>> => {
    const response: Promise<ApiResponse<LessonActivityComponentModel>> = httpClient.post(
      `/apis/lesson/component/duplicate/${lessonUuid}`,
      params,
      {},
    );
    return response;
  },
  removeContainer: (uuid, containerId) => {
    return httpClient.post(`/apis/lesson/removecontainer/${uuid}`, containerId, {});
  },
  reorderComponent: (lessonUuid, params) => {
    return httpClient.post(`/apis/lesson/rearrangecomponent/${lessonUuid}`, params);
  },
  saveViewCount: (params) => {
    return httpClient.post(`/apis/resource-view/save/lesson/${params.uuid}`);
  },
  renameAccordion: async (params) => {
    return await httpClient.post("/apis/lesson/accordion/rename", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  isComponentEditable: (pageId) => {
    return httpClient.get(`/apis/lesson/component/editable/${pageId}`);
  },
  isActivityQuizEditable: (componentId) => {
    return httpClient.get(`/apis/lesson/activity-quiz/editable/${componentId}`);
  },
  createSectionWithLessonTemplate: (params) => {
    return httpClient.post("/apis/section/create/with-template", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  createNewSection: (params) => {
    return httpClient.post("/apis/section/create", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  createNewSectionActivity: (params) => {
    return httpClient.post("/apis/section/add-activity", params);
  },
  getComponent: (componentId) => {
    return httpClient.get("/apis/lesson/component", {
      params: { componentId: componentId },
    });
  },
  getComponentErrorReason: (componentId) => {
    return httpClient.get("/apis/lesson/component/error-reason", {
      params: { componentId: componentId },
    });
  },

  /**
   * lesson api - QTI
   */
  createQti: (requestData): Promise<ApiResponse<string>> => {
    return httpClient.post(`/apis/lesson/qti-job/create`, requestData);
  },

  fetchQtiUploadJob: (qtiUploadJobUuid) => {
    return httpClient.get(`/apis/lesson/fetch/qti-upload-job/${qtiUploadJobUuid}`);
  },
  checkHasAnyProcessing: async (lessonUuid, resourceStatus): Promise<ApiResponse> => {
    if (resourceStatus === null || resourceStatus === undefined) {
      return await httpClient.get(`/apis/lesson/check-any-processing/${lessonUuid}`);
    }
    return await httpClient.get(
      `/apis/lesson/check-any-processing/${lessonUuid}?resourceStatus=${resourceStatus}`,
    );
  },
  // TODO: [Google] to review the usage
  checkHasAnyGoogleAttachment: (
    requestData: Partial<LessonResourceApiModel>[],
    googleDocumentRootFolderType: string,
  ): Promise<ApiResponse<boolean>> => {
    return httpClient.post(
      `/apis/lesson/check-any-google-attachment?googleDocumentRootFolderType=${googleDocumentRootFolderType}`,
      requestData,
    );
  },
  checkHasAnyProcessingInActivity: async (activityId): Promise<ApiResponse<boolean>> => {
    return await httpClient.get(`/apis/lesson/activity/check-any-processing/${activityId}`);
  },
  getLessonUuid: async (assignmentUuid): Promise<ApiResponse<LessonBasicInfo>> => {
    return await httpClient.get(`/apis/lesson/get/uuid/${assignmentUuid}`);
  },
  savePasscodeSettings: ({ lessonUuid, passcodeEnabled, passcode }) => {
    const formData = new FormData();
    formData.append("passcodeEnabled", passcodeEnabled);

    if (passcodeEnabled) {
      formData.append("passcode", passcode);
    }

    return httpClient.post(`/apis/lesson/passcode/save/${lessonUuid}`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  updateLessonTitle: (lessonUuid, title) => {
    return httpClient.post(`/apis/lesson/title/save/${lessonUuid}`, title, {});
  },
  updateLessonNotesAndDescription: (lessonUuid, params): Promise<ApiResponse> => {
    return httpClient.post(`/apis/lesson/notes-and-description/save/${lessonUuid}`, params);
  },
  updateSectionTitle: (sectionId, title) => {
    const formData = new FormData();
    formData.append("title", title);

    return httpClient.post(`/apis/section/title/save/${sectionId}`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  updateSectionTitleNotesAndDescription: (sectionId, params): Promise<ApiResponse> => {
    return httpClient.post(`/apis/section/title-notes-and-description/save/${sectionId}`, params);
  },
  fetchLessonAnchors: (lessonUuid: string): Promise<ApiResponse<LessonAnchorModel>> => {
    return httpClient.get(`/apis/lesson/activity/anchor/${lessonUuid}`);
  },
  isLessonAlsTagged: (lessonUuid) => {
    return httpClient.get(`/apis/lesson/is-als-tagged/${lessonUuid}`);
  },
  isSectionEditable: (sectionId) => {
    return httpClient.get(`/apis/lesson/section/editable/${sectionId}`);
  },
  isTeamAccessActivity: ({
    lessonId,
    activityId,
  }: {
    lessonId: number;
    activityId: number;
  }): Promise<ApiResponse<boolean>> => {
    return httpClient.get(`/apis/lesson/component/team-access`, {
      params: {
        lessonId,
        activityId,
      },
    });
  },
  checkLessonTeamActivityEligibility: async (
    activityIds: number[],
  ): Promise<ApiResponse<ActivityTeamEligibilityCheckResponse>> => {
    const params = new URLSearchParams();
    params.append("activityIds", activityIds.join(","));
    return await httpClient.get(`/apis/lesson/activity/team-eligibility/check`, {
      params,
    });
  },
};
